/**
 * Verification Requests — tenant-scoped verification history.
 *
 * Backend `require_tenant` guarantees an institution admin can only
 * ever see events bound to their own vlk_inst_id.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function VerificationRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (user?.institution_id) params.set("vlk_inst_id", user.institution_id);
    if (status) params.set("outcome", status);
    params.set("limit", "100");
    api.get(`/trust-events?${params}`)
      .then((r) => setRows(r.data?.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [user?.institution_id, status]);

  return (
    <div className="space-y-4" data-testid="institution-page-verifications">
      <div>
        <h1 className="text-xl font-semibold">Verification Requests</h1>
        <p className="text-sm text-muted-foreground">
          Trust events emitted for this institution. Every row is bound to a citizen consent + immutable audit_ref.
        </p>
      </div>

      <div className="flex items-center gap-2">
        {["", "GRANTED", "DENIED", "IDENTITY_LOCKED"].map((s) => (
          <Button key={s || "all"} size="sm"
                  variant={status === s ? "default" : "outline"}
                  onClick={() => setStatus(s)}
                  data-testid={`verifications-filter-${s || "all"}`}>
            {s || "All"}
          </Button>
        ))}
      </div>

      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="verifications-table">
          <thead className="bg-muted/30 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Timestamp</th>
              <th className="text-left p-3">Ledger token</th>
              <th className="text-left p-3">Action</th>
              <th className="text-left p-3">Outcome</th>
              <th className="text-left p-3">Fingerprint</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No verification events yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.trust_event_id || r._id} className="border-t">
                <td className="p-3 font-mono text-xs">{(r.timestamp || "").slice(0, 19)}</td>
                <td className="p-3 font-mono text-[10px]">{r.vlk_id_ledger_token?.slice(0, 22)}…</td>
                <td className="p-3 text-xs">{r.action}</td>
                <td className="p-3">
                  <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded ${
                    r.outcome === "GRANTED" ? "bg-green-500/15 text-green-700 dark:text-green-500"
                    : r.outcome === "DENIED" ? "bg-destructive/10 text-destructive"
                    : "bg-muted text-muted-foreground"
                  }`}>{r.outcome}</span>
                </td>
                <td className="p-3 font-mono text-[10px]">{r.cryptographic_fingerprint?.slice(0, 18)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
